import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { openPrintWindow } from "@/lib/printWindow";

function fmtEuro(n) {
  const v = Number(n || 0);
  return v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function isoToDmy(iso) { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; }

export default function ReportAddebitiPage() {
  const thisYear = new Date().getFullYear();
  const [anno, setAnno] = useState(thisYear);
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("data"); // data | causale
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [dettaglio, setDettaglio] = useState(null); // { tipo: 'data'|'causale', label, params }

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (anno) params.anno = anno;
      if (dataDa) params.data_da = isoToDmy(dataDa);
      if (dataA) params.data_a = isoToDmy(dataA);
      const r = await api.get("/contabilita/scadenzario-addebiti", { params });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento scadenzario");
    } finally { setLoading(false); }
  };

  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);
  // Reset ordinamento al cambio tab
  useEffect(() => { setSortBy(""); setSortDir("asc"); }, [tab]);

  const toggleSort = (col) => {
    if (sortBy === col) { setSortDir(d => d === "asc" ? "desc" : "asc"); return; }
    setSortBy(col); setSortDir("asc");
  };
  const SH = ({ col, label, align, style, defaultSort }) => {
    const active = (sortBy || defaultSort) === col;
    const arrow = active ? ((sortBy ? sortDir : "asc") === "asc" ? "▲" : "▼") : "↕";
    return (
      <th onClick={() => toggleSort(col)} data-testid={`sa-sort-${col}`}
        style={{ cursor: "pointer", userSelect: "none", textAlign: align || "left", ...(style || {}) }}
        title={`Ordina per ${label.toLowerCase()}`}>
        {label} <span style={{ fontSize: 9, color: active ? "#ffc000" : "#9ab6dd", marginLeft: 3 }}>{arrow}</span>
      </th>
    );
  };

  const parseDmy = (s) => {
    if (!s) return null;
    const [d, m, y] = s.split("/");
    if (!d || !m || !y) return null;
    return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
  };

  const rowsPerData = useMemo(() => {
    if (!data) return [];
    let rows = [...(data.per_data || [])];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter(r => (r.data_scad || "").toLowerCase().includes(q));
    const col = sortBy || "data_scad";
    const dir = sortBy ? sortDir : "asc";
    const fac = dir === "asc" ? 1 : -1;
    const get = (r) => {
      switch (col) {
        case "data_scad": return parseDmy(r.data_scad) ?? Number.POSITIVE_INFINITY;
        case "totale": return r.totale || 0;
        case "pagato": return r.pagato || 0;
        case "percentuale": return r.percentuale || 0;
        case "n_addebiti": return r.n_addebiti || 0;
        case "n_pagati": return r.n_pagati || 0;
        default: return 0;
      }
    };
    rows.sort((a, b) => {
      const va = get(a), vb = get(b);
      if (typeof va === "string" || typeof vb === "string") return String(va).localeCompare(String(vb), "it") * fac;
      return (va - vb) * fac;
    });
    return rows;
  }, [data, sortBy, sortDir, search]);

  const rowsPerCausale = useMemo(() => {
    if (!data) return [];
    let rows = [...(data.per_causale || [])];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(r =>
        (r.causale_descrizione || "").toLowerCase().includes(q) ||
        String(r.causale_codice || "").toLowerCase().includes(q) ||
        (r.gruppo_causale || "").toLowerCase().includes(q)
      );
    }
    const col = sortBy || "causale_codice";
    const dir = sortBy ? sortDir : "asc";
    const fac = dir === "asc" ? 1 : -1;
    const get = (r) => {
      switch (col) {
        case "gruppo": return (r.gruppo_causale || "ZZZ").toLowerCase();
        case "causale_codice": return r.causale_codice ?? 999999;
        case "causale_descrizione": return (r.causale_descrizione || "").toLowerCase();
        case "totale": return r.totale || 0;
        case "pagato": return r.pagato || 0;
        case "percentuale": return r.percentuale || 0;
        case "n_addebiti": return r.n_addebiti || 0;
        case "n_pagati": return r.n_pagati || 0;
        default: return 0;
      }
    };
    rows.sort((a, b) => {
      const va = get(a), vb = get(b);
      if (typeof va === "string" || typeof vb === "string") return String(va).localeCompare(String(vb), "it") * fac;
      return (va - vb) * fac;
    });
    return rows;
  }, [data, sortBy, sortDir, search]);

  const visibleTotals = useMemo(() => {
    if (tab === "pivot") {
      const rows = data?.per_data_gruppo || [];
      const tot = rows.reduce((s, r) => s + (r.totale_riga || 0), 0);
      const pag = rows.reduce((s, r) => s + (r.pagato_riga || 0), 0);
      const n = rows.reduce((s, r) => s + (r.n_addebiti_riga || 0), 0);
      return { tot, pag, n, count: rows.length, pct: tot > 0 ? (pag / tot * 100) : 0 };
    }
    const rows = tab === "data" ? rowsPerData : rowsPerCausale;
    const tot = rows.reduce((s, r) => s + (r.totale || 0), 0);
    const pag = rows.reduce((s, r) => s + (r.pagato || 0), 0);
    const n = rows.reduce((s, r) => s + (r.n_addebiti || 0), 0);
    return { tot, pag, n, count: rows.length, pct: tot > 0 ? (pag / tot * 100) : 0 };
  }, [tab, rowsPerData, rowsPerCausale, data]);

  const handleStampa = () => {
    const area = document.getElementById("scad-print-area");
    if (!area) return;
    const css = `
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; margin: 12px; color: #000; }
      h1 { font-size: 14px; margin: 0 0 4px 0; color: #1565c0; }
      .meta { font-size: 10px; color: #555; margin-bottom: 10px; }
      table { border-collapse: collapse; width: 100%; font-size: 10px; }
      th, td { border: 1px solid #999; padding: 3px 5px; vertical-align: middle; }
      th { background: #1f3864; color: #fff; font-weight: 700; text-align: center; }
      .num { text-align: right; font-family: Consolas, monospace; }
      tfoot td { font-weight: 700; background: #fff2cc; }
      .no-print { display: none !important; }
      @page { size: landscape; margin: 8mm; }
    `;
    const period = (dataDa || dataA) ? ` · ${isoToDmy(dataDa) || "…"} → ${isoToDmy(dataA) || "…"}` : "";
    const titolo = tab === "data" ? "Scadenzario Addebiti — riepilogo per data scadenza"
      : tab === "causale" ? "Scadenzario Addebiti — riepilogo per causale"
      : "Scadenzario Addebiti — pivot data × gruppo causale";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Scadenzario</title><style>${css}</style></head>
      <body>
        <h1>${titolo}</h1>
        <div class="meta">Anno ${anno || "—"}${period} · Stampato il ${new Date().toLocaleString("it-IT")}</div>
        ${area.innerHTML}
      </body></html>`;
    const w = openPrintWindow(html, { autoPrint: true });
    if (!w) { alert("Il browser ha bloccato la finestra di stampa."); }
  };

  return (
    <div>
      <div style={{ margin: "8px 20px", display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", background: "white", border: "1px solid #ccc", padding: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Anno competenza</div>
          <input type="number" data-testid="sa-anno" value={anno} onChange={e => setAnno(Number(e.target.value) || "")}
            style={{ width: 100, padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Scadenza da</div>
          <input type="date" data-testid="sa-da" value={dataDa} onChange={e => setDataDa(e.target.value)}
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>Scadenza a</div>
          <input type="date" data-testid="sa-a" value={dataA} onChange={e => setDataA(e.target.value)}
            style={{ padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <button data-testid="sa-carica" onClick={carica} disabled={loading}
          style={{ padding: "6px 18px", background: "#0066cc", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          {loading ? "Caricamento..." : "↻ Aggiorna"}
        </button>
        <div style={{ flex: "1 1 220px", minWidth: 200, opacity: tab === "pivot" ? 0.4 : 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 3 }}>
            Cerca {tab === "data" ? "data" : tab === "causale" ? "causale / gruppo" : "(non disponibile per pivot)"}
          </div>
          <div style={{ position: "relative" }}>
            <input type="text" data-testid="sa-search"
              value={search} onChange={e => setSearch(e.target.value)}
              disabled={tab === "pivot"}
              placeholder={tab === "data" ? "Es. 31/12/2025" : tab === "causale" ? "Es. retta, mensa..." : ""}
              style={{ width: "100%", padding: "4px 26px 4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
            {search && tab !== "pivot" && (
              <button type="button" onClick={() => setSearch("")} data-testid="sa-search-clear"
                title="Pulisci ricerca"
                style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14, padding: "0 4px" }}>
                ✕
              </button>
            )}
          </div>
        </div>
        <button onClick={handleStampa} disabled={!data}
          style={{ padding: "6px 14px", background: "white", color: "#1565c0", border: "1px solid #1565c0", fontSize: 12, fontWeight: 700, cursor: data ? "pointer" : "not-allowed", opacity: data ? 1 : 0.5 }}>
          🖨 Stampa
        </button>
        {data && (
          <div style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700 }}>
            {(search || tab) && (
              <>
                <span style={{ color: "#1565c0", marginRight: 8 }}>VISIBILI ({visibleTotals.count}):</span>
                <span style={{ color: "#1b5e20" }}>€ {fmtEuro(visibleTotals.tot)}</span>
                <span style={{ color: "#666", marginLeft: 8 }}>· Pagato: € {fmtEuro(visibleTotals.pag)} ({visibleTotals.pct.toFixed(2)}%)</span>
                <span style={{ fontWeight: 400, color: "#888", fontSize: 11, marginLeft: 8 }}>· {visibleTotals.n} addebiti</span>
              </>
            )}
          </div>
        )}
      </div>

      {err && <div style={{ margin: "0 20px 8px 20px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#cc0000", fontSize: 12 }}>{err}</div>}

      {data && (
        <div style={{ margin: "0 20px 4px 20px", display: "flex", gap: 0, borderBottom: "2px solid #1565c0" }}>
          <button data-testid="sa-tab-data" onClick={() => setTab("data")}
            style={{ padding: "8px 18px", border: "none", background: tab === "data" ? "#1565c0" : "#e3f2fd", color: tab === "data" ? "white" : "#1565c0", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: "4px 4px 0 0", marginRight: 2 }}>
            📅 Per data scadenza <span style={{ marginLeft: 6, padding: "1px 7px", background: tab === "data" ? "#0d47a1" : "white", borderRadius: 8, fontSize: 10 }}>{data.per_data?.length || 0}</span>
          </button>
          <button data-testid="sa-tab-causale" onClick={() => setTab("causale")}
            style={{ padding: "8px 18px", border: "none", background: tab === "causale" ? "#1565c0" : "#e3f2fd", color: tab === "causale" ? "white" : "#1565c0", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: "4px 4px 0 0" }}>
            🏷️ Per causale <span style={{ marginLeft: 6, padding: "1px 7px", background: tab === "causale" ? "#0d47a1" : "white", borderRadius: 8, fontSize: 10 }}>{data.per_causale?.length || 0}</span>
          </button>
          <button data-testid="sa-tab-pivot" onClick={() => setTab("pivot")}
            style={{ padding: "8px 18px", border: "none", background: tab === "pivot" ? "#1565c0" : "#e3f2fd", color: tab === "pivot" ? "white" : "#1565c0", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: "4px 4px 0 0", marginLeft: 2 }}>
            📊 Per data × gruppo <span style={{ marginLeft: 6, padding: "1px 7px", background: tab === "pivot" ? "#0d47a1" : "white", borderRadius: 8, fontSize: 10 }}>{data.per_data_gruppo?.length || 0}</span>
          </button>
          <div style={{ marginLeft: "auto", padding: "8px 0", fontSize: 12, color: "#444" }}>
            <b>TOTALE COMPLESSIVO</b>: <span style={{ color: "#1b5e20", fontWeight: 700 }}>€ {fmtEuro(data.grand_total)}</span>
            <span style={{ color: "#666", marginLeft: 8 }}>· Pagato: € {fmtEuro(data.grand_paid)} ({data.grand_percentuale}%)</span>
            <span style={{ color: "#888", fontSize: 11, marginLeft: 8 }}>· {data.grand_n_addebiti} addebiti</span>
          </div>
        </div>
      )}

      {data && (
        <div style={{ margin: "0 20px 20px 20px", background: "white", border: "1px solid #ccc", borderTop: "none" }}>
          <div id="scad-print-area" style={{ maxHeight: "70vh", overflow: "auto" }}>
            {tab === "data" && (
              <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
                <thead style={{ position: "sticky", top: 0, background: "#1f3864", color: "white", zIndex: 2 }}>
                  <tr>
                    <SH col="data_scad" label="DATA SCADENZA" defaultSort="data_scad" style={{ minWidth: 120 }} />
                    <SH col="totale" label="TOTALE" align="right" style={{ minWidth: 110 }} />
                    <SH col="pagato" label="PAGATO" align="right" style={{ minWidth: 110 }} />
                    <SH col="percentuale" label="% INC." align="right" style={{ minWidth: 80, background: "#ffc000", color: "#000" }} />
                    <SH col="n_addebiti" label="N° ADD." align="center" style={{ minWidth: 70 }} />
                    <SH col="n_pagati" label="N° PAG." align="center" style={{ minWidth: 70 }} />
                    <th className="no-print" style={{ width: 110, background: "#1565c0", color: "white", textAlign: "center" }}>DETTAGLIO</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsPerData.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessuna scadenza nel periodo.</td></tr>
                  )}
                  {rowsPerData.map((r, i) => {
                    const pctColor = r.percentuale >= 95 ? "#1b5e20" : r.percentuale >= 70 ? "#ef6c00" : "#c62828";
                    return (
                      <tr key={r.data_scad + "_" + i} data-testid={`sa-data-row-${i}`}>
                        <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700 }}>{r.data_scad || <span style={{ color: "#999", fontStyle: "italic" }}>(senza data)</span>}</td>
                        <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(r.totale)}</td>
                        <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(r.pagato)}</td>
                        <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 800, color: pctColor }}>{r.percentuale.toFixed(2)}%</td>
                        <td style={{ textAlign: "center" }}>{r.n_addebiti}</td>
                        <td style={{ textAlign: "center" }}>{r.n_pagati}</td>
                        <td className="no-print" style={{ textAlign: "center" }}>
                          <button data-testid={`sa-data-dett-${i}`}
                            onClick={() => setDettaglio({ tipo: "data", label: `Scadenza ${r.data_scad}`, params: { data_scad: r.data_scad } })}
                            style={{ background: "#1565c0", color: "white", border: "none", padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                            👁 Dettaglio
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {rowsPerData.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#263238", color: "white", fontWeight: 700 }}>
                      <td style={{ textAlign: "right", padding: "6px 8px" }}>TOTALE</td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(visibleTotals.tot)}</td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(visibleTotals.pag)}</td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>{visibleTotals.pct.toFixed(2)}%</td>
                      <td style={{ textAlign: "center" }}>{visibleTotals.n}</td>
                      <td></td>
                      <td className="no-print"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
            {tab === "causale" && (
              <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
                <thead style={{ position: "sticky", top: 0, background: "#1f3864", color: "white", zIndex: 2 }}>
                  <tr>
                    <SH col="gruppo" label="GRUPPO CAUSALE" style={{ minWidth: 160 }} />
                    <SH col="causale_codice" label="ID" defaultSort="causale_codice" align="center" style={{ width: 60 }} />
                    <SH col="causale_descrizione" label="CAUSALE" style={{ minWidth: 200 }} />
                    <SH col="totale" label="TOTALE" align="right" style={{ minWidth: 110 }} />
                    <SH col="pagato" label="PAGATO" align="right" style={{ minWidth: 110 }} />
                    <SH col="percentuale" label="% INC." align="right" style={{ minWidth: 80, background: "#ffc000", color: "#000" }} />
                    <SH col="n_addebiti" label="N° ADD." align="center" style={{ minWidth: 70 }} />
                    <SH col="n_pagati" label="N° PAG." align="center" style={{ minWidth: 70 }} />
                    <th className="no-print" style={{ width: 110, background: "#1565c0", color: "white", textAlign: "center" }}>DETTAGLIO</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsPerCausale.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessuna causale nel periodo.</td></tr>
                  )}
                  {rowsPerCausale.map((r, i) => {
                    const pctColor = r.percentuale >= 95 ? "#1b5e20" : r.percentuale >= 70 ? "#ef6c00" : "#c62828";
                    return (
                      <tr key={(r.causale_codice ?? "_") + "_" + i} data-testid={`sa-cau-row-${i}`}>
                        <td style={{ fontSize: 10, color: "#1565c0", fontWeight: 700 }}>{r.gruppo_causale || <span style={{ color: "#999" }}>(senza gruppo)</span>}</td>
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#37474f" }}>{r.causale_codice ?? "—"}</td>
                        <td>{r.causale_descrizione || <span style={{ color: "#999" }}>(senza descrizione)</span>}</td>
                        <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(r.totale)}</td>
                        <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(r.pagato)}</td>
                        <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 800, color: pctColor }}>{r.percentuale.toFixed(2)}%</td>
                        <td style={{ textAlign: "center" }}>{r.n_addebiti}</td>
                        <td style={{ textAlign: "center" }}>{r.n_pagati}</td>
                        <td className="no-print" style={{ textAlign: "center" }}>
                          <button data-testid={`sa-cau-dett-${i}`}
                            disabled={r.causale_codice === null || r.causale_codice === undefined}
                            onClick={() => setDettaglio({ tipo: "causale", label: `Causale ${r.causale_codice} · ${r.causale_descrizione || ""}`, params: { cau: r.causale_codice } })}
                            style={{ background: r.causale_codice == null ? "#bbb" : "#1565c0", color: "white", border: "none", padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: r.causale_codice == null ? "not-allowed" : "pointer", borderRadius: 2 }}>
                            👁 Dettaglio
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {rowsPerCausale.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#263238", color: "white", fontWeight: 700 }}>
                      <td colSpan={3} style={{ textAlign: "right", padding: "6px 8px" }}>TOTALE</td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(visibleTotals.tot)}</td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(visibleTotals.pag)}</td>
                      <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>{visibleTotals.pct.toFixed(2)}%</td>
                      <td style={{ textAlign: "center" }}>{visibleTotals.n}</td>
                      <td></td>
                      <td className="no-print"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
            {tab === "pivot" && (
              <PivotDataGruppoTable
                data={data}
                onCellClick={(dataScad, gruppo) => {
                  const codes = (gruppo.causali_codici || []).join(",");
                  const label = `Scadenza ${dataScad} · ${gruppo.descrizione}`;
                  const params = { data_scad: dataScad };
                  if (codes) params.cau_codes = codes;
                  setDettaglio({ tipo: "pivot", label, params });
                }}
              />
            )}
          </div>
        </div>
      )}

      {dettaglio && (
        <DettaglioModal
          tipo={dettaglio.tipo}
          label={dettaglio.label}
          baseParams={{
            ...(anno ? { anno } : {}),
            ...(dataDa ? { data_da: isoToDmy(dataDa) } : {}),
            ...(dataA ? { data_a: isoToDmy(dataA) } : {}),
            ...dettaglio.params,
          }}
          onClose={() => setDettaglio(null)}
        />
      )}
    </div>
  );
}

function DettaglioModal({ tipo, label, baseParams, onClose }) {
  const [rows, setRows] = useState([]);
  const [tot, setTot] = useState(0);
  const [pag, setPag] = useState(0);
  const [nopag, setNopag] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filtroPag, setFiltroPag] = useState("tutti"); // tutti | pagati | insoluti
  const [sortBy, setSortBy] = useState("data_scad");
  const [sortDir, setSortDir] = useState("asc");

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/contabilita/scadenzario-dettaglio", { params: baseParams });
      setRows(r.data.data || []);
      setTot(r.data.totale_importo || 0);
      setPag(r.data.pagato_importo || 0);
      setNopag(r.data.non_pagato_importo || 0);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento dettaglio");
    } finally { setLoading(false); }
  };

  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (filtroPag === "pagati") out = out.filter(r => r.pagato);
    else if (filtroPag === "insoluti") out = out.filter(r => !r.pagato);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(r =>
        (r.descrizione || "").toLowerCase().includes(q) ||
        (r.alunno || "").toLowerCase().includes(q) ||
        (r.causale_descrizione || "").toLowerCase().includes(q) ||
        String(r.alunno_codice || "").includes(q)
      );
    }
    const fac = sortDir === "asc" ? 1 : -1;
    const parseDmy = (s) => { if (!s) return null; const [d, m, y] = s.split("/"); return new Date(Number(y), Number(m) - 1, Number(d)).getTime(); };
    const get = (r) => {
      switch (sortBy) {
        case "data_scad": return parseDmy(r.data_scad) ?? Number.POSITIVE_INFINITY;
        case "data_pag": return parseDmy(r.data_pag) ?? Number.POSITIVE_INFINITY;
        case "alunno": return (r.alunno || "").toLowerCase();
        case "settore": return (r.alunno_settore || "").toLowerCase();
        case "descrizione": return (r.descrizione || "").toLowerCase();
        case "importo": return r.importo || 0;
        case "causale": return (r.causale_descrizione || String(r.causale_codice || "")).toLowerCase();
        case "pagato": return r.pagato ? 1 : 0;
        default: return 0;
      }
    };
    return [...out].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (typeof va === "string" || typeof vb === "string") return String(va).localeCompare(String(vb), "it") * fac;
      return (va - vb) * fac;
    });
  }, [rows, search, filtroPag, sortBy, sortDir]);

  const visTot = useMemo(() => filtered.reduce((s, r) => s + (r.importo || 0), 0), [filtered]);
  const visPag = useMemo(() => filtered.reduce((s, r) => r.pagato ? s + (r.importo || 0) : s, 0), [filtered]);

  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("asc"); } };
  const SH = ({ col, label, align, style }) => {
    const active = sortBy === col;
    const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "↕";
    return (
      <th onClick={() => toggleSort(col)} data-testid={`sad-sort-${col}`}
        style={{ cursor: "pointer", userSelect: "none", textAlign: align || "left", ...(style || {}) }}>
        {label} <span style={{ fontSize: 9, color: active ? "#ffc000" : "#9ab6dd", marginLeft: 3 }}>{arrow}</span>
      </th>
    );
  };

  const headerColor = tipo === "data" ? "#1565c0" : "#6a1b9a";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
      <div onClick={e => e.stopPropagation()} data-testid="sa-dettaglio-modal"
        style={{ background: "white", width: "100vw", height: "100vh", maxWidth: "100vw", maxHeight: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ background: headerColor, color: "white", padding: "5px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>👁 Dettaglio addebiti · {label}</div>
            <div style={{ fontSize: 10, opacity: 0.9 }}>Singoli movimenti · clicca header per ordinare</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer", padding: "0 4px" }}>✕</button>
        </div>
        <div style={{ padding: "6px 14px", borderBottom: "1px solid #ddd", background: "#f5f5f5", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca alunno, descrizione, causale..." data-testid="sad-search"
            style={{ flex: "1 1 250px", minWidth: 200, padding: "4px 10px", border: "1px solid #bbb", fontSize: 12 }} />
          <div style={{ display: "flex", gap: 0, border: "1px solid #bbb", borderRadius: 2, overflow: "hidden" }}>
            {[["tutti", "Tutti"], ["pagati", "✓ Pagati"], ["insoluti", "💰 Insoluti"]].map(([k, l]) => (
              <button key={k} onClick={() => setFiltroPag(k)} data-testid={`sad-filter-${k}`}
                style={{ padding: "4px 10px", border: "none", background: filtroPag === k ? headerColor : "white", color: filtroPag === k ? "white" : "#333", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                {l}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "#37474f" }}>
            <span>Visibili: <b>{filtered.length}</b></span>
            <span style={{ marginLeft: 10, color: "#1b5e20" }}>€ {fmtEuro(visTot)}</span>
            <span style={{ marginLeft: 8, color: "#666", fontWeight: 400 }}>(pagato € {fmtEuro(visPag)})</span>
            <span style={{ marginLeft: 12, color: "#888", fontWeight: 400, fontSize: 10 }}>· Tot: € {fmtEuro(tot)} · Pag: € {fmtEuro(pag)} · Non pag: € {fmtEuro(nopag)}</span>
          </div>
        </div>
        {err && <div style={{ margin: "6px 14px 0", padding: 8, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12, flexShrink: 0 }}>{err}</div>}
        <div style={{ flex: 1, overflow: "auto", padding: "0 4px", minHeight: 0 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun movimento.</div>
          ) : (
            <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
              <thead style={{ position: "sticky", top: 0, background: headerColor, color: "white", zIndex: 2 }}>
                <tr>
                  <SH col="data_scad" label="SCADENZA" style={{ minWidth: 90 }} />
                  <SH col="settore" label="PLESSO" align="center" style={{ width: 60 }} />
                  <SH col="alunno" label="ALUNNO" style={{ minWidth: 180 }} />
                  <SH col="descrizione" label="DESCRIZIONE" />
                  <SH col="causale" label="CAUSALE" style={{ minWidth: 130 }} />
                  <SH col="importo" label="IMPORTO" align="right" style={{ minWidth: 100 }} />
                  <SH col="pagato" label="STATO" align="center" style={{ width: 90 }} />
                  <SH col="data_pag" label="DATA PAG." style={{ minWidth: 90 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} data-testid={`sad-row-${i}`} style={r.pagato ? undefined : { background: "#fff8e1" }}>
                    <td style={{ fontFamily: "Consolas, monospace" }}>{r.data_scad || "—"}</td>
                    <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#1565c0" }}>{r.alunno_settore}</td>
                    <td>{r.alunno || <span style={{ color: "#999" }}>Alu {r.alunno_codice}</span>}</td>
                    <td>{r.descrizione || "—"}</td>
                    <td style={{ fontSize: 10 }}>
                      <span style={{ fontFamily: "Consolas, monospace", color: "#888", marginRight: 4 }}>#{r.causale_codice ?? "—"}</span>
                      {r.causale_descrizione}
                    </td>
                    <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: r.pagato ? "#1b5e20" : "#c62828" }}>€ {fmtEuro(r.importo)}</td>
                    <td style={{ textAlign: "center", fontWeight: 700, fontSize: 10 }}>
                      {r.pagato
                        ? <span style={{ padding: "1px 7px", background: "#e8f5e9", color: "#1b5e20", border: "1px solid #a5d6a7" }}>✓ PAGATO</span>
                        : <span style={{ padding: "1px 7px", background: "#fff3e0", color: "#c62828", border: "1px solid #ef9a9a" }}>💰 INSOLUTO</span>}
                    </td>
                    <td style={{ fontFamily: "Consolas, monospace", color: r.pagato ? "#1b5e20" : "#999" }}>{r.data_pag || "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#263238", color: "white", fontWeight: 700, position: "sticky", bottom: 0 }}>
                  <td colSpan={5} style={{ textAlign: "right", padding: "5px 8px" }}>TOTALE VISIBILI ({filtered.length})</td>
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(visTot)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function PivotDataGruppoTable({ data, onCellClick }) {
  const gruppi = data.gruppi_causali || [];
  const rows = data.per_data_gruppo || [];
  const totaliPerGruppo = data.totali_per_gruppo || {};
  const cellKey = (g) => g.codice === null || g.codice === undefined ? "_none" : String(g.codice);

  const grandTot = rows.reduce((s, r) => s + (r.totale_riga || 0), 0);
  const grandPag = rows.reduce((s, r) => s + (r.pagato_riga || 0), 0);
  const grandDaPag = grandTot - grandPag;

  if (!gruppi.length || !rows.length) {
    return <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun dato per il pivot data × gruppo causale.</div>;
  }

  // Stili comuni per le 3 sotto-colonne (totale/pagato/da pagare) di un gruppo
  const colStyles = {
    tot:  { bgHead: "#1f3864", bgSub: "#3a5078", colorSub: "#ffe082", bgCellPaid: "#fff9c4", bgCellPart: "#fff3e0", colorTxt: "#37474f" },
    pag:  { bgHead: "#1b5e20", bgSub: "#2e7d32", colorSub: "#a5d6a7", bgCellPaid: "#e8f5e9", bgCellPart: "#e8f5e9", colorTxt: "#1b5e20" },
    dapag:{ bgHead: "#c62828", bgSub: "#d32f2f", colorSub: "#ffcdd2", bgCellPaid: "#fafafa", bgCellPart: "#ffebee", colorTxt: "#c62828" },
  };

  return (
    <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
      <thead style={{ position: "sticky", top: 0, background: "#1f3864", color: "white", zIndex: 2 }}>
        <tr>
          <th rowSpan={3} style={{ minWidth: 110, textAlign: "left", padding: "6px 8px", verticalAlign: "middle" }}>DATA SCADENZA</th>
          {gruppi.map(g => (
            <th key={cellKey(g)} colSpan={3} style={{ textAlign: "center", padding: "4px 6px", borderLeft: "2px solid #0d2440" }}>
              <div style={{ fontWeight: 700 }}>
                {g.codice !== null && <span style={{ background: "#ffc000", color: "#000", padding: "1px 6px", borderRadius: 8, marginRight: 4, fontSize: 10 }}>{g.codice}</span>}
                {(g.descrizione || "").toUpperCase()}
              </div>
            </th>
          ))}
          <th colSpan={3} style={{ textAlign: "center", padding: "4px 6px", background: "#ffc000", color: "#000", borderLeft: "2px solid #0d2440" }}>TOTALE</th>
          <th rowSpan={3} className="no-print" style={{ width: 50, textAlign: "center", background: "#1565c0", verticalAlign: "middle" }}>📅</th>
        </tr>
        <tr>
          {gruppi.flatMap(g => [
            <th key={`${cellKey(g)}-h-t`}   style={{ background: colStyles.tot.bgHead,   color: "white", fontSize: 9, textAlign: "right", padding: "2px 4px", borderLeft: "2px solid #0d2440" }}>TOT</th>,
            <th key={`${cellKey(g)}-h-p`}   style={{ background: colStyles.pag.bgHead,   color: "white", fontSize: 9, textAlign: "right", padding: "2px 4px" }}>PAG</th>,
            <th key={`${cellKey(g)}-h-d`}   style={{ background: colStyles.dapag.bgHead, color: "white", fontSize: 9, textAlign: "right", padding: "2px 4px" }}>DA PAG.</th>,
          ])}
          <th style={{ background: colStyles.tot.bgHead,   color: "#ffe082", fontSize: 9, textAlign: "right", padding: "2px 4px", borderLeft: "2px solid #0d2440" }}>TOT</th>
          <th style={{ background: colStyles.pag.bgHead,   color: "#a5d6a7", fontSize: 9, textAlign: "right", padding: "2px 4px" }}>PAG</th>
          <th style={{ background: colStyles.dapag.bgHead, color: "#ffcdd2", fontSize: 9, textAlign: "right", padding: "2px 4px" }}>DA PAG.</th>
        </tr>
        <tr>
          {gruppi.flatMap(g => {
            const t = totaliPerGruppo[cellKey(g)];
            const tot = t?.totale || 0, pag = t?.pagato || 0, dapag = tot - pag;
            return [
              <th key={`${cellKey(g)}-s-t`} style={{ textAlign: "right", background: colStyles.tot.bgSub,   color: colStyles.tot.colorSub,   fontSize: 10, padding: "2px 6px", borderLeft: "2px solid #0d2440" }}>€ {fmtEuro(tot)}</th>,
              <th key={`${cellKey(g)}-s-p`} style={{ textAlign: "right", background: colStyles.pag.bgSub,   color: colStyles.pag.colorSub,   fontSize: 10, padding: "2px 6px" }}>€ {fmtEuro(pag)}</th>,
              <th key={`${cellKey(g)}-s-d`} style={{ textAlign: "right", background: colStyles.dapag.bgSub, color: colStyles.dapag.colorSub, fontSize: 10, padding: "2px 6px" }}>€ {fmtEuro(dapag)}</th>,
            ];
          })}
          <th style={{ textAlign: "right", background: colStyles.tot.bgSub,   color: colStyles.tot.colorSub,   fontSize: 10, padding: "2px 6px", borderLeft: "2px solid #0d2440" }}>€ {fmtEuro(grandTot)}</th>
          <th style={{ textAlign: "right", background: colStyles.pag.bgSub,   color: colStyles.pag.colorSub,   fontSize: 10, padding: "2px 6px" }}>€ {fmtEuro(grandPag)}</th>
          <th style={{ textAlign: "right", background: colStyles.dapag.bgSub, color: colStyles.dapag.colorSub, fontSize: 10, padding: "2px 6px" }}>€ {fmtEuro(grandDaPag)}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const daPagRiga = (r.totale_riga || 0) - (r.pagato_riga || 0);
          return (
            <tr key={r.data_scad + "_" + i} data-testid={`sa-pivot-row-${i}`}>
              <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, padding: "3px 8px" }}>
                {r.data_scad || <span style={{ color: "#999", fontStyle: "italic" }}>(senza data)</span>}
              </td>
              {gruppi.flatMap(g => {
                const cell = r.celle[cellKey(g)];
                const v = cell?.totale || 0;
                const pag = cell?.pagato || 0;
                const dapag = v - pag;
                const hasValue = !!cell;
                const fullyPaid = hasValue && Math.abs(dapag) < 0.01;
                const cs = colStyles;
                const baseCellTot = { textAlign: "right", padding: "2px 6px", fontFamily: "Consolas, monospace", borderLeft: "2px solid #1f3864" };
                const baseCellSub = { textAlign: "right", padding: "2px 6px", fontFamily: "Consolas, monospace" };
                return [
                  <td key={`${r.data_scad}-${cellKey(g)}-t`}
                    style={{ ...baseCellTot,
                             background: hasValue ? (fullyPaid ? cs.tot.bgCellPaid : cs.tot.bgCellPart) : undefined,
                             color: hasValue ? cs.tot.colorTxt : "#bbb",
                             fontWeight: hasValue ? 700 : 400, cursor: hasValue ? "pointer" : "default" }}
                    onClick={() => { if (hasValue) onCellClick(r.data_scad, g); }}
                    title={hasValue ? `${cell.n_addebiti} addebiti · clicca per dettaglio` : ""}>
                    {hasValue ? `€ ${fmtEuro(v)}` : "—"}
                  </td>,
                  <td key={`${r.data_scad}-${cellKey(g)}-p`}
                    style={{ ...baseCellSub,
                             background: hasValue && pag !== 0 ? cs.pag.bgCellPaid : undefined,
                             color: hasValue && pag !== 0 ? cs.pag.colorTxt : "#bbb",
                             fontWeight: hasValue && pag !== 0 ? 600 : 400 }}
                    title={hasValue ? `${cell.n_pagati}/${cell.n_addebiti} pagati` : ""}>
                    {hasValue && pag !== 0 ? `€ ${fmtEuro(pag)}` : "—"}
                  </td>,
                  <td key={`${r.data_scad}-${cellKey(g)}-d`}
                    style={{ ...baseCellSub,
                             background: hasValue && Math.abs(dapag) > 0.01 ? cs.dapag.bgCellPart : undefined,
                             color: hasValue && Math.abs(dapag) > 0.01 ? cs.dapag.colorTxt : "#bbb",
                             fontWeight: hasValue && Math.abs(dapag) > 0.01 ? 600 : 400 }}
                    title={hasValue ? `${(cell.n_addebiti || 0) - (cell.n_pagati || 0)} insoluti` : ""}>
                    {hasValue && Math.abs(dapag) > 0.01 ? `€ ${fmtEuro(dapag)}` : "—"}
                  </td>,
                ];
              })}
              <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, background: "#fff9c4", color: "#1b5e20", padding: "3px 8px", borderLeft: "2px solid #1f3864" }}>
                € {fmtEuro(r.totale_riga)}
              </td>
              <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, background: "#e8f5e9", color: "#1b5e20", padding: "3px 8px" }}>
                € {fmtEuro(r.pagato_riga)}
              </td>
              <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700,
                           background: Math.abs(daPagRiga) > 0.01 ? "#ffebee" : "#fafafa",
                           color: Math.abs(daPagRiga) > 0.01 ? "#c62828" : "#bbb", padding: "3px 8px" }}>
                € {fmtEuro(daPagRiga)}
              </td>
              <td className="no-print" style={{ textAlign: "center", padding: "2px 4px" }}>
                <button data-testid={`sa-pivot-row-dett-${i}`}
                  onClick={() => onCellClick(r.data_scad, { codice: "ALL", descrizione: "Tutti i gruppi", causali_codici: [] })}
                  title="Dettaglio di tutti gli addebiti di questa scadenza"
                  style={{ background: "#1565c0", color: "white", border: "none", padding: "2px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                  👁
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr style={{ background: "#263238", color: "white", fontWeight: 700, position: "sticky", bottom: 0 }}>
          <td style={{ padding: "5px 8px", textAlign: "right" }}>TOTALI PERIODO</td>
          {gruppi.flatMap(g => {
            const t = totaliPerGruppo[cellKey(g)];
            const tot = t?.totale || 0, pag = t?.pagato || 0, dapag = tot - pag;
            return [
              <td key={`tot-${cellKey(g)}-t`} style={{ textAlign: "right", padding: "5px 6px", fontFamily: "Consolas, monospace", borderLeft: "2px solid #0d2440", color: "#ffe082" }}>€ {fmtEuro(tot)}</td>,
              <td key={`tot-${cellKey(g)}-p`} style={{ textAlign: "right", padding: "5px 6px", fontFamily: "Consolas, monospace", color: "#a5d6a7" }}>€ {fmtEuro(pag)}</td>,
              <td key={`tot-${cellKey(g)}-d`} style={{ textAlign: "right", padding: "5px 6px", fontFamily: "Consolas, monospace", color: Math.abs(dapag) > 0.01 ? "#ef9a9a" : "#9e9e9e" }}>€ {fmtEuro(dapag)}</td>,
            ];
          })}
          <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", padding: "5px 8px", background: "#ffc000", color: "#000", fontSize: 12, borderLeft: "2px solid #0d2440" }}>
            € {fmtEuro(grandTot)}
          </td>
          <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", padding: "5px 8px", background: "#a5d6a7", color: "#1b5e20", fontSize: 12 }}>
            € {fmtEuro(grandPag)}
          </td>
          <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", padding: "5px 8px", background: Math.abs(grandDaPag) > 0.01 ? "#ef9a9a" : "#cfd8dc", color: "#b71c1c", fontSize: 12 }}>
            € {fmtEuro(grandDaPag)}
          </td>
          <td className="no-print"></td>
        </tr>
      </tfoot>
    </table>
  );
}

